import { ROLES, PERMISSIONS, getRoleByName } from '../constants/roles.js';

/**
 * Check if a user has a specific permission
 * @param {Object} user - User object with role property
 * @param {string} permission - Permission to check
 * @returns {boolean} - Whether user has the permission
 */
export const hasPermission = (user, permission) => {
  if (!user || !user.role) {
    return false;
  }

  const role = getRoleByName(user.role);
  return role.permissions.includes(permission);
};

/**
 * Check if a user has any of the specified permissions
 * @param {Object} user - User object with role property
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} - Whether user has any of the permissions
 */
export const hasAnyPermission = (user, permissions) => {
  return permissions.some(permission => hasPermission(user, permission));
};

/**
 * Check if a user has all of the specified permissions
 * @param {Object} user - User object with role property
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} - Whether user has all of the permissions
 */
export const hasAllPermissions = (user, permissions) => {
  return permissions.every(permission => hasPermission(user, permission));
};

/**
 * Check if a user has a specific role
 * @param {Object} user - User object with role property
 * @param {string} roleName - Role name to check
 * @returns {boolean} - Whether user has the role
 */
export const hasRole = (user, roleName) => {
  return user && user.role === roleName;
};

/**
 * Check if a user is an admin
 * @param {Object} user - User object with role property
 * @returns {boolean} - Whether user is an admin
 */
export const isAdmin = (user) => {
  return hasRole(user, ROLES.ADMIN.name);
};

/**
 * Check if a user is a talent partner
 * @param {Object} user - User object with role property
 * @returns {boolean} - Whether user is a talent partner
 */
export const isTalentPartner = (user) => {
  return hasRole(user, ROLES.TALENT_PARTNER.name);
};

/**
 * Get all permissions for a user's role
 * @param {Object} user - User object with role property
 * @returns {string[]} - Array of permissions
 */
export const getUserPermissions = (user) => {
  if (!user || !user.role) {
    return [];
  }

  const role = getRoleByName(user.role);
  return role.permissions;
};

/**
 * Filter data based on user permissions
 * @param {Object} user - User object with role property
 * @param {Object} data - Data object to filter
 * @returns {Object} - Filtered data based on user permissions
 */
export const filterDataByPermissions = (user, data) => {
  if (!user || !data) {
    return {};
  }

  const filteredData = { ...data };

  // Filter NPS data based on permissions
  if (data.npsData && !hasPermission(user, PERMISSIONS.VIEW_INDIVIDUAL_CLIENT_NPS)) {
    // Remove individual client NPS scores for non-admin users
    filteredData.npsData = {
      ...data.npsData,
      individualScores: undefined,
      clientSpecificData: undefined,
      aggregatedData: data.npsData.aggregatedData || data.npsData
    };
  }

  // Filter user management data
  if (data.users && !hasPermission(user, PERMISSIONS.MANAGE_USERS)) {
    filteredData.users = undefined;
  }

  // Filter audit logs
  if (data.auditLogs && !hasPermission(user, PERMISSIONS.VIEW_AUDIT_LOGS)) {
    filteredData.auditLogs = undefined;
  }

  return filteredData;
};

/**
 * Check if user can access a specific route/component
 * @param {Object} user - User object with role property
 * @param {string} route - Route name or component identifier
 * @returns {boolean} - Whether user can access the route
 */
export const canAccessRoute = (user, route) => {
  const routePermissions = {
    '/admin': [PERMISSIONS.MANAGE_USERS],
    '/admin/users': [PERMISSIONS.MANAGE_USERS],
    '/admin/clients': [PERMISSIONS.MANAGE_CLIENT_ACCESS],
    '/admin/audit': [PERMISSIONS.VIEW_AUDIT_LOGS],
    '/nps/individual': [PERMISSIONS.VIEW_INDIVIDUAL_CLIENT_NPS],
    '/candidates': [PERMISSIONS.VIEW_CANDIDATES],
    '/analytics': [PERMISSIONS.VIEW_ANALYTICS],
    '/pipeline': [PERMISSIONS.VIEW_PIPELINE],
    '/client-dashboard': [PERMISSIONS.VIEW_OWN_PIPELINE]
  };

  const requiredPermissions = routePermissions[route];
  if (!requiredPermissions) {
    return true; // Allow access to routes without specific permissions
  }

  return hasAnyPermission(user, requiredPermissions);
};
